import React from 'react';
import { useTranslation } from 'next-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import StarRating from '@components/UI/molecules/StarRating';
import Textarea from '@components/UI/atoms/form/Textarea';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { ReviewModalProps } from './review-modal.types';

const STAR_SIZE = '2.2em';

export const ReviewModal = ({
  totalSatisfaction,
  audioQuality,
  reviewText,
  isSubmitting,
  message,
  messageType,
  onTotalSatisfactionChange,
  onAudioQualityChange,
  onReviewTextChange,
  onSubmit,
  onCancel,
}: ReviewModalProps) => {
  const { t } = useTranslation('common');

  const textareaProps = {
    placeholder: t('modals.review.reviewPlaceholder'),
    className:
      'max-w-lg shadow-sm block w-full sm:text-sm border border-light-gray rounded-md py-2 px-4',
    value: reviewText,
    name: 'review',
    rows: 5,
    updateHandler: onReviewTextChange,
  };

  return (
    <div className="max-w-xl mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('modals.review.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={onCancel}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="post"
            className="class-rating-form"
            onSubmit={onSubmit}
          >
            <div className="control-group mb-4">
              <h4 className="mb-4 text-lg font-medium">
                1. {t('modals.review.totalSatisfactionSentence')}
              </h4>
              <StarRating
                name="total"
                canEdit={true}
                fontSize={STAR_SIZE}
                rating={totalSatisfaction}
                handleRate={onTotalSatisfactionChange}
              />
            </div>
            <div className="control-group mb-4">
              <h4 className="mb-4 text-lg font-medium">
                2. {t('modals.review.audioQualitySentence')}
              </h4>
              <StarRating
                name="audio"
                canEdit={true}
                fontSize={STAR_SIZE}
                rating={audioQuality}
                handleRate={onAudioQualityChange}
              />
            </div>
            <div className="control-group mb-4">
              <h4 className="mb-4 text-lg font-medium">
                3. {t('modals.review.reviewSentence')}
              </h4>
              <Textarea {...textareaProps} />
            </div>
            <div className="control-group flex justify-end">
              <Button size="lg" type="submit" disabled={isSubmitting}>
                {t('modals.review.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
